import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { space, SpaceProps } from 'styled-system';
import fluidFunc from 'fluid-system';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';
import { timingFunctions } from 'polished';

const Container = styled.div<StyledUnderlineProps>`
    ${({ fluid }) => (fluid ? fluidFunc(space) : space)}
    overflow: hidden;
    display: inline-block;
    line-height: 0;
    position: relative;

    > *:last-child {
        display: inline-block;

        margin-bottom: calc(
            6px + ${({ verticalSpacing }) => verticalSpacing || 0.1}em
        );

        ::after {
            content: '';
            position: absolute;
            display: inline-block;
            height: ${({ thickness }) => thickness || 6}px;
            width: calc(
                100% + ${({ extendedWidth }) => extendedWidth || '0px'}
            );
            background: ${({ theme, color }) =>
                color || theme.colors.primary.main};
            left: 0;
            bottom: 0;
            transform: translateX(-100%);
            transition: all ${({ duration }) => duration || '4'}s ${timingFunctions('ease')};
        }
    }

    ${({ active }) =>
        active &&
        css`
            > *:last-child {
                ::after {
                    transform: translateX(0);
                }
            }
        `}
`;

interface StyledUnderlineProps extends SpaceProps {
    extendedWidth?: string;
    thickness?: number;
    verticalSpacing?: number;
    fluid?: boolean;
    color?: string;
    active?: boolean;
    duration?: number
}

const Underline: React.FC<StyledUnderlineProps> = ({
    children,
    extendedWidth,
    verticalSpacing,
    thickness,
    color,
    duration,
    ...props
}) => {
    const [containerRef, inView] = useInView({
        triggerOnce: true,
        threshold: 0.8,
    });

    return (
        <Container
            active={inView}
            ref={containerRef}
            verticalSpacing={verticalSpacing}
            extendedWidth={extendedWidth}
            thickness={thickness}
            color={color}
            duration={duration}
            {...props}
        >
            {children}
        </Container>
    );
};

export default Underline;
